import axios from 'axios';
import { getCookie } from '../utils/authUtils';
import { captureException, addSentryBreadcrumb } from '../utils/sentry';
import { useAppStore } from '../store/useAppStore';
class MarketChefAPI {
  constructor() {
    // Use environment variable for API base URL
    this.baseURL = import.meta.env.VITE_API_BASE_URL;
    this.accessKey = getCookie('auth_token');
    this.instagramClientId = '1141120864610972';
    this.facebookAppId = '2779803235696822';
    this.client = axios.create({
      baseURL: this.baseURL,
      timeout: 15000,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${this.accessKey}`,
      },
    });

    // Request interceptor to add Bearer token
    this.client.interceptors.request.use((config) => {
      if (this.accessKey) {
        config.headers.Authorization = `Bearer ${this.accessKey}`;
      }
      return config;
    });

    // Response interceptor for error handling
    this.client.interceptors.response.use(
      (response) => response.data,
      (error) => {
        if (error.response) {
          // If the error is 401, redirect to the login page
          if (error.response.status === 401) {
            useAppStore.getState().setIsAuthenticated(false);
          }

          // Send error to Sentry for tracking
          const errorMessage = error.response.data?.message || error.message;
          captureException(error, {
            api: {
              method: error.config?.method?.toUpperCase(),
              url: error.config?.url,
              status: error.response?.status,
              statusText: error.response?.statusText,
            },
          });

          // Add breadcrumb for debugging
          addSentryBreadcrumb(
            `API Error: ${error.response.status}`,
            {
              method: error.config?.method?.toUpperCase(),
              url: error.config?.url,
              message: errorMessage,
            },
            'error'
          );
        } else if (error.request) {
          // Request made but no response
          captureException(error, {
            api: {
              type: 'no_response',
              method: error.config?.method?.toUpperCase(),
              url: error.config?.url,
            },
          });
        }
        console.error('API Error:', error);
        return Promise.reject(error);
      }
    );
  }

  setAccessKey(accessKey) {
    this.accessKey = accessKey;
  }

  // Generic request method to match PHP client
  async request(method, endpoint, body = null) {
    const config = {
      method: method.toUpperCase(),
      url: endpoint,
    };

    if (body !== null) {
      config.data = body;
    }

    try {
      const response = await this.client.request(config);
      return response;
    } catch (error) {
      // Throw the axios error directly - it already has all the info we need
      // (error.response.status, error.response.data, error.message)
      throw error;
    }
  }

  // ==================== API METHODS ====================

  // Authentication
  async activateAccessKey(accessKey) {
    return this.request('POST', '/v1/auth/key/activate', { access_key: accessKey });
  }

  async getUserDetails() {
    const result = await this.request('GET', '/v1/user');
    return result;
  }

  async getUserIdentity() {
    const result = await this.request('GET', '/v1/user/identity');
    return result;
  }

  async getUserSubscription() {
    const result = await this.request('GET', '/v1/user/subscription');
    return result;
  }

  // Onboarding
  async createProfileFromWebsite(websiteUrl) {
    const result = await this.request('POST', '/v1/profile/from-website', { website: websiteUrl });
    return result;
  }

  // Profile Management
  async checkProfileReady() {
    const result = await this.request('GET', '/v1/profile');
    return result;
  }

  async checkProfileSummaryReady() {
    const result = await this.request('GET', '/v1/profile/summary');
    return result;
  }

  async checkOfferingsReady() {
    const result = await this.request('GET', '/v1/profile/offering');
    return result;
  }

  async deleteProfileOffering(offeringId) {
    return this.request('DELETE', `/v1/profile/offering/${offeringId}`);
  }
  async updateProfileOffering(offeringId, offeringData) {
    return this.request('PUT', `/v1/profile/offering/${offeringId}`, offeringData);
  }

  async createProfileOffering(offeringData) {
    return this.request('POST', '/v1/profile/offering', offeringData);
  }

  async checkPostsReady() {
    const result = await this.request('GET', '/v1/posts');
    return result;
  }

  async fetchProfile() {
    return this.request('GET', '/v1/profile');
  }

  async fetchProfileSummary() {
    const a = this.request('GET', '/v1/profile/summary');
    return a;
  }

  async fetchProfileOfferings() {
    return this.request('GET', '/v1/profile/offering');
  }

  async updateProfileSummary(summaryData) {
    return this.request('PUT', '/v1/profile/summary', summaryData);
  }

  // Posts Management
  async fetchPosts(profileId) {
    return this.request('GET', `/v1/posts/${profileId}`);
  }

  async updatePost(postId, postData) {
    return this.request('PUT', `/v1/posts/${postId}`, postData);
  }

  async regeneratePost(postUid, userInput) {
    return this.request('POST', `/v1/posts/${postUid}/regenerate`, { userInput });
  }

  async getPostDetails(postId) {
    return this.request('GET', `/v1/posts/${postId}`);
  }

  async approvePost(postId, socialAccountsPlatforms = [], timezone = '') {
    const data = {
      socialAccountsPlatforms,
      timezone,
    };

    return this.request('POST', `/v1/posts/${postId}/approve`, data);
  }

  async cancelPost(postId, socialAccountsPlatforms = [], timezone = '') {
    const data = {
      socialAccountsPlatforms,
      timezone,
    };
    return this.request('POST', `/v1/posts/${postId}/approve`, data);
  }

  // Social Media
  async fetchSocialAccounts() {
    return this.request('GET', '/v1/social/accounts');
  }

  // async fetchInstagramInsights() {
  //   return this.request('GET', '/v1/social/instagram/insights');
  // }

  // Profile editing
  async editProfile(profileId, profileData) {
    return this.request('POST', `/profile/${profileId}/edit`, profileData);
  }

  //https://www.instagram.com/oauth/authorize?force_reauth=true&client_id=1141120864610972&redirect_uri=https://api.postreams.com/v1/social/instagram/callback&response_type=code&scope=instagram_business_basic%2Cinstagram_business_manage_messages%2Cinstagram_business_manage_comments%2Cinstagram_business_content_publish%2Cinstagram_business_manage_insights
  async connectInstagramAccount() {
    const redirectUri = `${this.baseURL}/v1/social/instagram/callback`;
    const scopes = encodeURIComponent(
      'instagram_business_basic,instagram_business_content_publish'
    );

    const stateObj = {
      access_key: getCookie('auth_token'),
      intr: `${window.location.protocol}//${window.location.host}/socials`,
    };
    const state = encodeURIComponent(JSON.stringify(stateObj));
    const authUrl = `https://www.instagram.com/oauth/authorize?force_reauth=true&client_id=${this.instagramClientId}&redirect_uri=${redirectUri}&response_type=code&scope=${scopes}&state=${state}`;

    localStorage.setItem('instagram_oauth_state', state);

    window.location.href = authUrl;
  }

  async connectFacebookAccount() {
    const backendRedirect = `${this.baseURL}/v1/social/facebook/callback`;
    const scopes = [
      'business_management',
      'pages_show_list',
      'pages_manage_posts',
      'instagram_basic',
      'instagram_content_publish',
    ].join(',');

    const stateObj = {
      access_key: getCookie('auth_token'),
      intr: `${window.location.protocol}//${window.location.host}/socials`,
    };

    const state = encodeURIComponent(JSON.stringify(stateObj));
    const authUrl = `https://www.facebook.com/v23.0/dialog/oauth?client_id=${this.facebookAppId}&redirect_uri=${backendRedirect}&scope=${scopes}&response_type=code&state=${state}`;

    window.location.href = authUrl;
  }

  // OAuth Callback Handlers
  async handleInstagramCallback(code, state) {
    return this.request('POST', '/v1/social/instagram/callback', {
      code,
      state,
    });
  }

  async handleFacebookCallback(code, state) {
    return this.request('POST', '/v1/social/facebook/callback', {
      code,
      state,
    });
  }

  // Disconnect accounts
  async disconnectAccount(accountId) {
    return this.request('DELETE', `/v1/social-account/${accountId}`);
  }

  async disconnectSocial(platform) {
    return this.request('DELETE', `/v1/social/${platform}/disconnect`);
  }

  async sendFeedback(message, post_id) {
    return this.request('POST', '/v1/user/feedback', { message, post_id });
  }
}

export const api = new MarketChefAPI();
